import { useState, useRef, useEffect } from 'react'
import { LogOut, Shield, User } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'

interface ProfileDropdownProps {
  user: any
  onProfileClick?: () => void
  isAdmin?: boolean
}

export function ProfileDropdown({ user, onProfileClick, isAdmin }: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
      navigate('/')
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  const displayName = `${user.user_metadata?.first_name || user.first_name || ''} ${user.user_metadata?.last_name || user.last_name || ''}`.trim()
  const avatarUrl = user.user_metadata?.avatar_url || user.avatar_url || ''
  const username = user.username || user.user_metadata?.username || user.email?.split('@')[0] || ''

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-9 h-9 rounded-full overflow-hidden bg-gray-100 hover:ring-2 hover:ring-gray-300 transition-all focus:outline-none focus:ring-2 focus:ring-gray-400"
      >
        {avatarUrl ? (
          <img 
            src={avatarUrl} 
            alt={displayName || user.email}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || user.email)}&size=36&background=e5e7eb&color=6b7280`
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm text-gray-500 font-semibold">
            {(displayName || user.email || '').charAt(0).toUpperCase()}
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
          >
            {/* User Info */}
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">
                {displayName || user.email}
              </p>
              <p className="text-xs text-gray-500 truncate">
                @{username}
              </p>
            </div>

            {/* Menu Items */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsOpen(false)
                if (onProfileClick) {
                  onProfileClick()
                } else {
                  navigate(`/profile/${username}`)
                }
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
            >
              <User className="h-4 w-4" />
              My Profile
            </button>

            {isAdmin && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setIsOpen(false)
                  navigate('/admin')
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <Shield className="h-4 w-4" />
                Admin
              </button>
            )}

            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Log Out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}